import React from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { z } from 'zod'
import { useAuthStore } from '../store/authStore'
import presidio_logo from '../assets/presidio_logo.png'
import './LoginPage.css'

const loginSchema = z.object({
  username: z.string()
    .transform((val) => val.trim()) // Trim whitespace
    .refine((val) => val.length > 0, 'Username is required')
    .refine((val) => val.length >= 3, 'Username must be at least 3 characters')
    .refine((val) => /^[a-zA-Z0-9._-]+$/.test(val), 'Username can only contain letters, numbers, dots, underscores, and hyphens'),
  password: z.string()
    .transform((val) => val.trim()) // Trim whitespace
    .refine((val) => val.length > 0, 'Password is required')
    .refine((val) => val.length >= 6, 'Password must be at least 6 characters')
    .refine((val) => val.length <= 50, 'Password must be less than 50 characters'),
  rememberMe: z.boolean().optional(),
})

type LoginFormData = z.infer<typeof loginSchema>

const LoginPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { signIn } = useAuthStore()

  // Add state for login error and button loading
  const [loginError, setLoginError] = React.useState<string>('')
  const [isSigningIn, setIsSigningIn] = React.useState<boolean>(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur', // Validate on blur for better UX
  })

  const onSubmit = async (data: LoginFormData) => {
    try {
      // Clear any previous errors and start loading
      setLoginError('')
      setIsSigningIn(true)

      // Additional client-side validation
      const trimmedUsername = data.username.trim()
      const trimmedPassword = data.password.trim()

      if (!trimmedUsername) {
        setLoginError('Username cannot be empty or just whitespace')
        setIsSigningIn(false)
        return
      }

      if (!trimmedPassword) {
        setLoginError('Password cannot be empty or just whitespace')
        setIsSigningIn(false)
        return
      }

      await signIn(trimmedUsername, trimmedPassword, !!data.rememberMe)

      // Success - stop loading
      setIsSigningIn(false)

      // Redirect to the intended page or dashboard
      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    } catch (error: any) {

      console.error('Error message:', error.message)


      // Provide more specific error messages
      let errorMessage = 'Login failed. Please check your credentials and try again.'

      // Handle both legacy __type and modern name properties
      const errorType = error.__type || error.name || error.code
      console.error('Detected error type:', errorType)

      switch (errorType) {
        case 'NotAuthorizedException':
        case 'NotAuthorized':
          errorMessage = 'Authentication failed. Incorrect username or password.'
          break
        case 'UserNotConfirmedException':
        case 'UserNotConfirmed':
          errorMessage = 'Your account needs to be confirmed. Please check your email for confirmation instructions.'
          break
        case 'PasswordResetRequiredException':
        case 'PasswordResetRequired':
          errorMessage = 'Password reset is required. Please contact your administrator.'
          break
        case 'UserNotFoundException':
        case 'UserNotFound':
          errorMessage = 'Username not found. Please check your username and try again.'
          break
        case 'TooManyRequestsException':
        case 'TooManyRequests':
          errorMessage = 'Too many login attempts. Please wait a few minutes before trying again.'
          break
        case 'UserLambdaValidationException':
          errorMessage = 'Login validation failed. Please contact your administrator.'
          break
        case 'InvalidParameterException':
        case 'InvalidParameter':
          errorMessage = 'Invalid login parameters. Please check your input and try again.'
          break
        case 'InternalErrorException':
        case 'InternalError':
          errorMessage = 'An internal error occurred. Please try again later.'
          break
        default:
          if (error.message) {
            errorMessage = error.message
          }
          break
      }



      // Simply set the error message
      setLoginError(errorMessage)



      // Stop loading on error
      setIsSigningIn(false)
    }
  }


  return (
    <div className="login-background flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-lg w-full">
        {/* Header with Logo and Branding */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center text-white shield-icon-container">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="lock-icon">
              <path d="M6 10V8a6 6 0 1 1 12 0v2h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h1zm2 0h8V8a4 4 0 1 0-8 0v2z"/>
            </svg>
          </div> 
          <div className="mx-auto mb-4 flex flex-col items-center justify-center">
            <p className="data-trust-accelerator-title">Data Trust Accelerator</p>
          </div>
          <p className="text-gray-500 text-sm">Sign in to access your account</p>
        </div>

        {/* Login Form */}
        <div className="login-form">

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
            {/* Validation Summary */}
            {(errors.username || errors.password) && (
              <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
                <div className="flex items-start">
                  <span className="text-yellow-500 text-sm mr-2">ℹ</span>
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Please fix the following:</h4>
                    <ul className="mt-1 text-xs text-yellow-700 list-disc list-inside space-y-1">
                      {errors.username && <li>Username: {errors.username.message}</li>}
                      {errors.password && <li>Password: {errors.password.message}</li>}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-gray-700 mb-2 text-xl font-medium">
                Username or Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  {...register('username')}
                  type="text"
                  autoComplete="username"
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-2xl ${
                    errors.username
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter your username"
                  onBlur={(e) => {
                    // Trim the value on blur
                    e.target.value = e.target.value.trim()
                  }}
                />
              </div>
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.username.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 mb-2 text-xl font-medium">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  {...register('password')}
                  type="password"
                  autoComplete="current-password"
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-2xl ${
                    errors.password
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter your password"
                  onBlur={(e) => {
                    // Trim the value on blur
                    e.target.value = e.target.value.trim()
                  }}
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Login Error Message - Below Password Field */}
            {loginError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-sm">
                <div className="flex items-start">
                  <div>
                    <p className="text-sm text-red-700 font-medium">
                      {loginError}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-6">
              <Link to="/forgot-password" className="text-sm text-teal-500 hover:text-teal-600">
                Forgot password?
              </Link>
            </div>


            <button
              type="submit"
              disabled={isSigningIn}
              className={`w-full py-3 text-white font-medium rounded-lg text-sm transition-all duration-200 sign-in-button ${
                isSigningIn
                  ? 'cursor-not-allowed opacity-75'
                  : 'hover:shadow-lg hover:opacity-90'
              }`}
            >
              {isSigningIn ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        {/* Presidio Logo at bottom */}
        <div className="flex justify-center mt-6 pt-4 border-gray-100">
          <img src={presidio_logo} alt="PRESIDIO" className="presidio-logo-image" />
        </div>
      </div>
    </div>
  )
}

export default LoginPage
